﻿import React, { Fragment, useEffect, useState } from 'react';
import { Route, Navigate, Routes, useParams, useLocation } from 'react-router-dom';
import { VerticalLinkGroup, Breadcrumbs } from 'components/navigation';
import { JobDetails } from 'pages/job/edit/job-details';
import { JobNotes } from 'pages/job/edit/notes';
import { JobChangelog } from 'pages/job/edit/changelog';
import { UserAccess, UserHasAccess } from 'components/user-access';
import { PageRestricted } from 'pages/errors/page-restricted';
import { JobService, LocationService, OperatingHoursService } from 'services';
import { NotificationToaster, NotificationInline } from "components/notifications";
import { Intent } from '@blueprintjs/core';
import { LocationDetails } from './location';
import { ListingTable } from "components/listing";
import axios from 'axios';
import moment from "moment";
import { CloseJob } from './close-job';
import { ShowHide } from "components/layout";
import { LinkButton } from 'components/buttons';
import _ from 'underscore';

export function Job(props) {
    const timeFormat = "YYYY-MM-DD HH:mm:ss";
    const inductionDateTime = "01 Jan 2000";

    const addressModel = {
        locationId: 0,
        addressLine1: "",
        addressLine2: "",
        addressLine3: "",
        postTown: "",
        county: "",
        postCode: "",
        phoneNumber: ""
    };

    const additionalInfoModel = {
        locationId: null,
        weighPadAvailable: null,
        inductionRequired: false,
        inductionDurationTime: null,
        inductionTime: 0
    };

    const { jobId } = useParams();
    const location = useLocation();
    const [additionalInfo, setAdditionalInfo] = useState(additionalInfoModel);
    const [fromParam, setFromParam] = useState(null);
    const requiredAction = ["ScheduledAppointment:View", "Internal"];
    const [loadingData, setLoadingData] = useState(true);
    const canAccessPage = UserHasAccess(requiredAction);
    const isInternal = UserHasAccess("Internal");
    const [jobDetails, setJobDetails] = useState({});
    const [address, setAddress] = useState(addressModel);
    const [jobNote, setJobNote] = useState('');
    const [additionalPropertiesMapped, setAdditionalPropertiesMapped] = useState(false);
    const [operatingHours, setOperatingHours] = useState([]);
    const contactTableHeaders = ["Email Address", "First Name", "Last Name", "Phone Number", "Business Area"];
    const [contactTableRows, setContactTableRows] = useState([]);
    const [contactRecordCount, setContactRecordCount] = useState(0);   
    const noDataMessage = "You have no contacts.";
    const [hasTachoContact, setHasTachoContact] = useState(null);
    const [invalidJob, setInvalidJob] = useState(false);

    // Store the from parameter when first detected
    useEffect(() => {
        const searchParams = new URLSearchParams(location.search);
        const currentFromParam = searchParams.get('from');
        
        if (currentFromParam && !fromParam) {
            setFromParam(currentFromParam);
        }
    }, [location.search, fromParam]);
    
    // Create breadcrumbs based on where the user came from
    const getBreadcrumbs = () => {
        if (fromParam === 'contracts') {
            return [
                { location: "/job-administration?tab=contracts", title: "job administration" },
                { title: "Job" }
            ];
        }
        return props.breadcrumbs; // Use default breadcrumbs
    };

    const breadcrumbs = getBreadcrumbs();

    var routes = [
        { location: `/job${props.baseUrl}/${jobId}/job-details`, title: "Job Details" },
        { location: `/job${props.baseUrl}/${jobId}/location-details`, title: "Visit Location Details"},
        { location: `/job${props.baseUrl}/${jobId}/contacts`, title: "Contacts" }
    ];

    if (isInternal) {
        routes.push({ location: `/job${props.baseUrl}/${jobId}/job-notes`, title: "Internal Notes", selected: false });
        routes.push({ location: `/job${props.baseUrl}/${jobId}/changes`, title: "History" });
    }

    useEffect(() => {
        if (!canAccessPage) {
            return;
        }
        setLoadingData(true);
        var failed = false;
        JobService.getJob(jobId).then(function (jobResponse) {
            
            setJobDetails(jobResponse);
            setAddress({
                ...address,
                locationId: jobResponse.locationId
            });

            if (jobResponse.locationId === 0) {
                setLoadingData(false);
                setHasTachoContact(false);
                return;
            }

            axios.all([LocationService.getLocationAdditionalInformation(jobResponse.locationId),
                LocationService.getContacts(jobResponse.locationId)
            ])
                .then(axios.spread(function (additionalInfoResponse, contactResonse) {

                    if (additionalInfoResponse.address) {
                        setAddress(additionalInfoResponse.address);
                    }

                    var locationBusinessArea = additionalInfoResponse.businessAreaAdditionalDetails.find(e => e.locationBusinessAreaId === jobResponse.jobProductBusinessAreaId);

                    if (locationBusinessArea !== undefined) {
                        setOperatingHours(OperatingHoursService.getFullWeekOperatingHours(locationBusinessArea.operatingHours, additionalPropertiesMapped, setAdditionalPropertiesMapped));
                        setJobNote(locationBusinessArea.notes);
                    }

                    var hours = 0;
                    var mins = 0;
                    if (additionalInfoResponse.additionalInfo.inductionTime !== undefined && additionalInfoResponse.additionalInfo.inductionTime > 0) {
                        hours = Math.floor(additionalInfoResponse.additionalInfo.inductionTime / 60);
                        mins = additionalInfoResponse.additionalInfo.inductionTime - (hours * 60);
                    }

                    var cloneAdditionalInfo = {
                        ...additionalInfoResponse.additionalInfo,
                        inductionDurationTime: moment(inductionDateTime).startOf('day').set('hour', hours).set('minute', mins).format(timeFormat),
                        locationId: jobResponse.locationId,
                        inductionTime: additionalInfoResponse.additionalInfo.inductionTime == null ? 0 : additionalInfoResponse.additionalInfo.inductionTime
                    };
                    
                    setAdditionalInfo(cloneAdditionalInfo);
                    setContactsRows(contactResonse);
                    setContactRecordCount(contactResonse.length)
                    setLoadingData(false);
                    setHasTachoContact(hasContactOfTypeTacho(contactResonse));
                }), (error) => {
                    failed = true;
                });

            setLoadingData(false);
        }, (error) => {
            setLoadingData(false);
            failed = true;

            const noAccessCode = 403;
            const badRequestCode = 400;
            // bad request can occur when job id is invalid not for the user account.
            // an issue was raised to change this from 403 in the API to 400. So handle both here.
            if (error.message.includes(noAccessCode) || error.message.includes(badRequestCode)) {
                setInvalidJob(true);
            }
        });

        if (failed) {
            NotificationToaster.show(Intent.DANGER, "Could not get the address information. Please refresh the page.");
        }
    }, [jobId]);

    function onSave(modifiedJob) {
        setJobDetails(modifiedJob);
    }

    function onCloseJobSave(modifiedJob) {
        setJobDetails(modifiedJob);
    }

    function hasContactOfTypeTacho(contactResponse) {
        return (contactResponse.length > 0 && contactResponse.find(e => e.contactType === "Tacho") !== undefined);
    }

    function setContactsRows(contacts) {
        let data = [];

        if (contacts.length > 0) {
            data = contacts.map((d) => {

                return [d.emailAddress, d.firstName,d.lastName, d.phoneNumber, d.contactType];
            })
        }

        setContactTableRows(data);
    }
    return (


        <UserAccess perform={requiredAction}
            yes={() => (

                <ShowHide
                    evaluator={invalidJob}
                    show={(
                        <div className="row">
                            <Breadcrumbs items={breadcrumbs} />
                            <NotificationInline
                                allowClose={false}
                                show
                                text="You do not have permission to view this job"
                                intent="danger">
                            </NotificationInline>
                            <div>
                                <LinkButton intent="primary" text={`Back to ${breadcrumbs[0].title}`} href={breadcrumbs[0].location} id="return-job-listing" />
                            </div>
                        </div>
                    )}
                    hide={(
                        <Fragment>
                            <div className="row">
                                <Breadcrumbs items={breadcrumbs} />
                                {<h1>{"Reference " + jobId}</h1>}
                                <div className="intro-text">
                                    <p>Here you can view and configure your job details.</p>
                                </div>
                            </div>

                            <div className="layout-cols">
                                <div className="col side-col">
                                    <div className="row">
                                        <VerticalLinkGroup links={routes}></VerticalLinkGroup>
                                    </div>
                                </div>
                                <div className="col content-col">
                                    <Routes>
                                        <Route path="job-details" element={<JobDetails job={jobDetails} hasTachoContact={hasTachoContact} onSave={onSave} id={jobId}></JobDetails>} />
                                        <Route path="location-details" element={<LocationDetails addressSettings={{ address: address }} loading={loadingData} operatingHours={operatingHours} additionalInfo={{ additionalInfo }} id={jobId}></LocationDetails>} />
                                        <Route path="contacts" element={<div className="row">
                                                <h2>Contact Details</h2>
                                                <div className="spacer-bottom">
                                                    <ListingTable
                                                        id="listing-table-contacts"
                                                        headers={contactTableHeaders}
                                                        data={contactTableRows}
                                                        loadingData={loadingData}
                                                        noDataMessage={noDataMessage}
                                                        pageable={false}
                                                        totalRecordCount={contactRecordCount}
                                                    />
                                                </div>
                                            </div>} />
                                        <Route path="job-notes" element={<JobNotes job={jobDetails} locationNotes={jobNote} onSave={onSave}></JobNotes>} />
                                        <Route path="changes" element={<JobChangelog jobId={jobId}></JobChangelog>} />
                                        <Route path="close-job" element={<CloseJob job={jobDetails} onSave={onCloseJobSave} id={jobId}></CloseJob>} />
                                        <Route path="" element={<Navigate to={`/job${props.baseUrl}/${jobId}/job-details`} replace />} />
                                    </Routes>
                                </div>
                            </div>
                        </Fragment>
                    )}
                />
            )}
            no={() => (
                <PageRestricted />
            )}
        />
    );

}